import { useState, useCallback, useEffect } from 'react';
import { useCanvasStore } from '../stores/canvasStore';
import { useStudioStore } from '../stores/studioStore';
import { useFontStore } from '../stores/fontStore';
import { useFontAliasStore } from '../stores/fontAliasStore';
import { loadCustomFont } from '../lib/fontLoader';
import { parseSvgFile, parseSvgFileDecomposed, parseSvgFileSmartMerge, analyzeSvgForSmartMerge, applyFontAssignments } from '../lib/svgImport';
import { matchAllSvgFonts, type FontMatchResult } from '../lib/svgFontMatcher';
import { guardCanvasStateForSave } from '../lib/canvasStateGuard';
import type { CanvasElement, TextElement } from '../types';
import type { FontAssignment } from '../components/editor/FontMatchDialog';

export function useSvgImportFlow() {
  // State declarations
  const [isImporting, setIsImporting] = useState(false);
  const [importStatus, setImportStatus] = useState('');
  const [svgImportPending, setSvgImportPending] = useState<{
    file: File;
    elementCount: number;
  } | null>(null);
  const [fontMatchPending, setFontMatchPending] = useState<{
    elements: CanvasElement[];
    matchResults: FontMatchResult[];
  } | null>(null);

  // commitImportedElements (lines 606-635)
  const commitImportedElements = useCallback((elements: CanvasElement[]) => {
    if (elements.length === 0) return;
    for (const el of elements) {
      if (el.type === 'text') {
        const t = el as TextElement;
        console.log(`[SVG Font] Element committed to store: "${t.text?.substring(0, 30)}" fontFamily="${t.fontFamily}" fontWeight=${t.fontWeight}`);
      }
    }
    useStudioStore.getState().pushUndoState();
    const store = useStudioStore.getState();
    const merged = [...store.canvasState.elements, ...elements];
    // Defense-in-depth: strip any inline base64 image payloads that slipped
    // through SVG import, so they never reach canvas_state / the save RPC.
    const guarded = guardCanvasStateForSave({ ...store.canvasState, elements: merged });
    if (guarded.removedImageSrc > 0 || guarded.removedSvgImages > 0) {
      console.warn('[Import] Stripped inline base64 images from imported elements:',
        { imageSrc: guarded.removedImageSrc, svgImages: guarded.removedSvgImages });
    }
    const newElements = guarded.state.elements;
    const lastId = newElements[newElements.length - 1]?.id ?? elements[elements.length - 1].id;
    useStudioStore.setState({
      canvasState: { ...store.canvasState, elements: newElements },
      selectedElementId: lastId,
      selectedElementIds: [lastId],
      redoStack: [],
    });
    useStudioStore.getState()._scheduleAutoSave();
    useStudioStore.getState()._scheduleThumbnailGeneration();
    useCanvasStore.getState().setActiveTool('select');
  }, []);

  // runFontMatching (lines 637-715)
  const runFontMatching = useCallback(async (elements: CanvasElement[], files: File[]) => {
    setIsImporting(true);
    setImportStatus('Loading font library...');
    try {
      await useFontStore.getState().refreshFonts();
      await useFontAliasStore.getState().ensureLoaded();
      setImportStatus('Matching fonts...');
      let svgString = '';
      for (const file of files) {
        svgString += await file.text();
      }
      const aliases = useFontAliasStore.getState().aliases;
      const matchResults = matchAllSvgFonts(svgString, aliases);
      if (matchResults.length === 0) {
        setImportStatus('Rendering elements...');
        commitImportedElements(elements);
        setTimeout(() => { setIsImporting(false); setImportStatus(''); }, 400);
        return;
      }
      const needsAttention = matchResults.some(r => !r.matched || r.needsManualVariantSelection);
      if (!needsAttention) {
        const autoAssignments: FontAssignment[] = matchResults.map(r => ({
          originalCss: r.originalCss,
          assignedFamily: r.matchedFamily,
          assignedWeight: r.matchedWeight,
          assignedStyle: r.matchedStyle,
          assignedVariantId: r.matchedVariantId,
          skipped: false,
        }));

        const existingByOriginal = new Map(aliases.map((a) => [a.originalCss, a]));
        const autoAliasWrites = matchResults.filter(
          (r) =>
            r.matched &&
            r.matchTier === 'exact' &&
            r.matchedFamily &&
            r.matchedWeight !== null &&
            !existingByOriginal.has(r.originalCss)
        );
        if (autoAliasWrites.length > 0) {
          Promise.all(
            autoAliasWrites.map((r) =>
              useFontAliasStore.getState().upsertAlias({
                originalCss: r.originalCss,
                assignedFamily: r.matchedFamily!,
                assignedWeight: r.matchedWeight,
                assignedStyle: r.matchedStyle ?? 'normal',
              })
            )
          ).catch((err) => console.warn('[SVG Font] auto-alias persist failed:', err));
        }
        const finalElements = applyFontAssignments(elements, autoAssignments) as CanvasElement[];

        setImportStatus('Loading fonts...');
        const fontFamiliesToLoad = new Set<string>();
        for (const el of finalElements) {
          if (el.type === 'text' && (el as TextElement).fontFamily) {
            fontFamiliesToLoad.add((el as TextElement).fontFamily);
          }
        }
        if (fontFamiliesToLoad.size > 0) {
          console.log(`[SVG Font] Pre-loading ${fontFamiliesToLoad.size} custom fonts:`, Array.from(fontFamiliesToLoad));
          await Promise.all(Array.from(fontFamiliesToLoad).map(f => loadCustomFont(f)));
        }

        setImportStatus('Rendering elements...');
        commitImportedElements(finalElements);
        setTimeout(() => { setIsImporting(false); setImportStatus(''); }, 400);
        return;
      }
      setIsImporting(false);
      setImportStatus('');
      setFontMatchPending({ elements, matchResults });
    } catch (error) {
      console.error('SVG font matching failed:', error);
      alert('SVG font matching failed: ' + (error instanceof Error ? error.message : 'An unexpected error occurred. Check the console for details.'));
      setIsImporting(false);
      setImportStatus('');
    }
  }, [commitImportedElements]);

  // handleFontMatchConfirm (lines 717-746)
  const handleFontMatchConfirm = useCallback(async (assignments: FontAssignment[]) => {
    if (fontMatchPending) {
      setIsImporting(true);
      setImportStatus('Applying font assignments...');
      try {
        const finalElements = applyFontAssignments(fontMatchPending.elements, assignments) as CanvasElement[];

        setImportStatus('Loading fonts...');
        const fontFamiliesToLoad = new Set<string>();
        for (const el of finalElements) {
          if (el.type === 'text' && (el as TextElement).fontFamily) {
            fontFamiliesToLoad.add((el as TextElement).fontFamily);
          }
        }
        if (fontFamiliesToLoad.size > 0) {
          console.log(`[SVG Font] Pre-loading ${fontFamiliesToLoad.size} custom fonts:`, Array.from(fontFamiliesToLoad));
          await Promise.all(Array.from(fontFamiliesToLoad).map(f => loadCustomFont(f)));
        }

        setImportStatus('Rendering elements...');
        commitImportedElements(finalElements);
        setTimeout(() => { setIsImporting(false); setImportStatus(''); }, 400);
      } catch (error) {
        console.error('SVG font assignment failed:', error);
        setIsImporting(false);
        setImportStatus('');
      }
    }
    setFontMatchPending(null);
  }, [fontMatchPending, commitImportedElements]);

  // handleFontMatchCancel (lines 748-753)
  const handleFontMatchCancel = useCallback(() => {
    if (fontMatchPending) {
      commitImportedElements(fontMatchPending.elements);
    }
    setFontMatchPending(null);
  }, [fontMatchPending, commitImportedElements]);

  // handleSvgImportDecomposed (lines 755-785)
  const handleSvgImportDecomposed = useCallback(async (files: File[]) => {
    setIsImporting(true);
    setImportStatus('Analyzing SVG...');
    try {
      const cw = useCanvasStore.getState().canvasWidth;
      const ch = useCanvasStore.getState().canvasHeight;
      let currentLen = useStudioStore.getState().canvasState.elements.length;

      const allElements: CanvasElement[] = [];
      for (const file of files) {
        const result = await parseSvgFileDecomposed(file, { width: cw, height: ch }, currentLen);
        if (result.success && result.elements) {
          for (const el of result.elements) {
            allElements.push(el as CanvasElement);
          }
          currentLen += result.elements.length;
        }
      }

      if (allElements.length > 0) {
        await runFontMatching(allElements, files);
      } else {
        setIsImporting(false);
        setImportStatus('');
      }
    } catch (error) {
      console.error('SVG decomposed import failed:', error);
      alert('SVG import (decomposed) failed: ' + (error instanceof Error ? error.message : 'An unexpected error occurred. Check the console for details.'));
      setIsImporting(false);
      setImportStatus('');
    }
  }, [runFontMatching]);

  // handleSvgImportGrouped (lines 787-815)
  const handleSvgImportGrouped = useCallback(async (files: File[]) => {
    setIsImporting(true);
    setImportStatus('Analyzing SVG...');
    try {
      const cw = useCanvasStore.getState().canvasWidth;
      const ch = useCanvasStore.getState().canvasHeight;
      let currentLen = useStudioStore.getState().canvasState.elements.length;

      const allElements: CanvasElement[] = [];
      for (const file of files) {
        const result = await parseSvgFile(file, { width: cw, height: ch }, currentLen);
        if (result.success && result.elementData) {
          allElements.push(result.elementData as unknown as CanvasElement);
          currentLen++;
        }
      }

      if (allElements.length > 0) {
        await runFontMatching(allElements, files);
      } else {
        setIsImporting(false);
        setImportStatus('');
      }
    } catch (error) {
      console.error('SVG grouped import failed:', error);
      alert('SVG import (grouped) failed: ' + (error instanceof Error ? error.message : 'An unexpected error occurred. Check the console for details.'));
      setIsImporting(false);
      setImportStatus('');
    }
  }, [runFontMatching]);

  // handleSvgImportSmartMerge (lines 817-847)
  const handleSvgImportSmartMerge = useCallback(async (files: File[]) => {
    setIsImporting(true);
    setImportStatus('Analyzing SVG...');
    try {
      const cw = useCanvasStore.getState().canvasWidth;
      const ch = useCanvasStore.getState().canvasHeight;
      let currentLen = useStudioStore.getState().canvasState.elements.length;

      const allElements: CanvasElement[] = [];
      for (const file of files) {
        const result = await parseSvgFileSmartMerge(file, { width: cw, height: ch }, currentLen);
        if (result.success && result.elements) {
          for (const el of result.elements) {
            allElements.push(el as CanvasElement);
          }
          currentLen += result.elements.length;
        }
      }

      if (allElements.length > 0) {
        await runFontMatching(allElements, files);
      } else {
        setIsImporting(false);
        setImportStatus('');
      }
    } catch (error) {
      console.error('SVG smart merge import failed:', error);
      alert('SVG import (smart merge) failed: ' + (error instanceof Error ? error.message : 'An unexpected error occurred. Check the console for details.'));
      setIsImporting(false);
      setImportStatus('');
    }
  }, [runFontMatching]);

  // cs:import-svg useEffect (lines 920-962)
  useEffect(() => {
    const handler = async () => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.svg,image/svg+xml';
      input.multiple = false;
      input.onchange = async () => {
        const files = input.files;
        if (!files || files.length === 0) return;
        const file = files[0];

        try {
          setIsImporting(true);
          setImportStatus('Analyzing SVG file...');

          const cw = useCanvasStore.getState().canvasWidth;
          const ch = useCanvasStore.getState().canvasHeight;
          const currentLen = useStudioStore.getState().canvasState.elements.length;
          const result = await parseSvgFileDecomposed(file, { width: cw, height: ch }, currentLen);

          if (!result.success || !result.elements) {
            console.error('[SVG Import] Pre-analysis failed:', result.error || 'Unknown error');
            setIsImporting(false);
            setImportStatus('');
            alert(`SVG import failed: ${result.error || 'Could not parse SVG file. The file may use unsupported features.'}`);
            return;
          }

          setIsImporting(false);
          setImportStatus('');
          setSvgImportPending({ file, elementCount: result.elements.length });
        } catch (err) {
          console.error('[SVG Import] Unexpected error during pre-analysis:', err);
          setIsImporting(false);
          setImportStatus('');
          alert('SVG import failed: An unexpected error occurred while reading the file.');
        }
      };
      input.click();
    };
    document.addEventListener('cs:import-svg', handler);
    return () => document.removeEventListener('cs:import-svg', handler);
  }, []);

  return {
    isImporting,
    importStatus,
    svgImportPending,
    setSvgImportPending,
    fontMatchPending,
    setFontMatchPending,
    handleFontMatchConfirm,
    handleFontMatchCancel,
    handleSvgImportDecomposed,
    handleSvgImportGrouped,
    handleSvgImportSmartMerge,
  };
}

export { analyzeSvgForSmartMerge } from '../lib/svgImport';
